import { CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import { IMAGES } from "@/data/site";

const EXPERIENCE_AREAS = [
    "Construction of residential and commercial buildings",
    "Renovation and repair works",
    "Maintenance assessment and planning",
    "Contractor billing and quantity review",
    "BOQ preparation and verification",
    "Landscaping and garden works",
    "Site assessment and progress review",
    "Technical documentation and reporting",
];

const PRINCIPLES = [
    {
        title: "Independent",
        text: "The consultancy does not execute construction work. With no stake in the contractor's bill, every observation and recommendation serves only the client's interest.",
    },
    {
        title: "Professional",
        text: "Engagements are scoped in writing, observations are documented, and every review closes with a structured report carrying a unique Report ID.",
    },
    {
        title: "Practical",
        text: "Recommendations are grounded in how work actually happens on site — focused on decisions you can act on, not theory.",
    },
];

export default function About() {
    return (
        <div>
            <Seo
                title="About Us | The Sens Civil Works Consultancy — Gorakhpur"
                description="38 years of practical civil works experience — construction, maintenance, billing, quantity review and landscaping consultancy in Gorakhpur, Uttar Pradesh."
            />
            <PageHero
                overline="About the Consultancy"
                title="38 Years of Practical Civil Works Experience"
                description="The Sens Civil Works Consultancy provides independent civil engineering and construction consultancy for homeowners, property owners, housing societies, institutions, architects and small developers."
            />

            <section className="bg-white py-20 lg:py-28" data-testid="about-story">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:px-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="text-lg leading-relaxed text-navy">
                                Led by <strong>P. Sen Gupta</strong>, the consultancy brings nearly four decades of
                                hands-on involvement in civil works — from supervising construction and maintenance to
                                checking contractor bills and verifying quantities on site.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                Over 38 years, one lesson repeats itself: most construction disputes and cost overruns
                                begin with decisions taken without independent technical input. A quotation approved
                                without review, a bill paid without verification, workmanship accepted without
                                inspection. This consultancy exists to give clients that independent input — before
                                the decision, not after the dispute.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                Based in Gorakhpur, Uttar Pradesh, the practice serves homeowners building or
                                renovating, property owners planning maintenance, architects and small developers
                                needing technical support, and housing societies and institutions that require
                                documented, independent review of works and bills.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-14 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
                                Areas of Experience
                            </h2>
                        </Reveal>
                        <div className="mt-8 grid grid-cols-1 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2">
                            {EXPERIENCE_AREAS.map((area, i) => (
                                <Reveal key={area} delay={Math.min(i * 0.05, 0.3)} className="bg-white">
                                    <div className="flex h-full items-start gap-3 p-5">
                                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-amber" />
                                        <span className="text-sm leading-relaxed text-slate-700">{area}</span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            <div className="tick-frame sticky top-28">
                                <img
                                    src={IMAGES.engineer.url}
                                    alt={IMAGES.engineer.alt}
                                    className="aspect-[4/5] w-full object-cover"
                                    loading="lazy"
                                    data-testid="about-image"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="blueprint-grid border-t border-slate-200 bg-background py-20 lg:py-28" data-testid="about-principles">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">How We Work</p>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            Independent advice. Practical assessment. Clear documentation.
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {PRINCIPLES.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.08} className="h-full">
                                <div className="h-full border border-slate-200 bg-white p-8">
                                    <p className="font-mono text-sm font-semibold tracking-[0.2em] text-amber">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-4 font-display text-xl font-bold text-navy">{p.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaSection
                overline="Work With Us"
                title="Bring 38 Years of Experience to Your Project"
                description="Start with a consultation — describe your project and the decision you are facing, and get practical, independent guidance."
            />
        </div>
    );
}
